import React, { useEffect, useState, useRef } from 'react';
import styled, { css } from 'styled-components/macro';
import { Link } from 'react-router-dom';
import { connect } from 'react-redux';
import translate from '../../../components/TranslateHOC';
import ListItemSubtitlesV2 from './ListItemSubtitlesV2';
import { imageFallback, isOnNow, durationInMins } from '../../../helpers';
import { Icon as IconV2 } from '../icon/Icon';
import { SEARCH_IMG_THUMBNAIL_URL } from '../../../constants';

export const ListItemV2 = ({
  strings,
  details,
  curLang,
  isChild,
  parent,
  isToday,
}) => {
  const [currentTime, setCurrentTime] = useState(
    String(new Date().getHours()).padStart(2, '0') +
      ':' +
      String(new Date().getMinutes()).padStart(2, '0')
  );
  const progLink = `/programme/${details.programme_id}`;
  const prevTimeRef = useRef('');
  const isOnNowCurrent =
    isToday && isOnNow(currentTime, details.time, details.duration);

  useEffect(() => {
    const id = setInterval(() => {
      if (prevTimeRef.current !== currentTime) {
        setCurrentTime(currentTime);
        prevTimeRef.current = currentTime;
      }
    }, 1000);
    return () => clearInterval(id);
  }, [currentTime]);

  return (
    <ListCardItem>
      {details.available ? (
        <CustomLink
          className="list-card__item-link-programme--tvschedule"
          to={progLink}
        >
          <ListCard>
            <ListCardTime lang={curLang}>
              <Time isOnNow={isOnNowCurrent}>{details.time}</Time>
              {isOnNowCurrent && <OnNow>{strings.onNow}</OnNow>}
            </ListCardTime>

            <Image
              src={SEARCH_IMG_THUMBNAIL_URL + details.thumbnail}
              alt={details.title}
              onError={(e) =>
                imageFallback(e, SEARCH_IMG_THUMBNAIL_URL + 'clic-c.jpg')
              }
            />
            <ListCardDescription>
              {isChild && <ParentTitle>{parent}</ParentTitle>}
              <Title>{details.title}</Title>
              <BillingDesc>{details.short_billing}</BillingDesc>

              <Tags>
                <ListItemSubtitlesV2 details={details} strings={strings} />
                {details.duration && (
                  <>
                    <IconV2 type="custom" customLabel="timer" size="small" />
                    <Duration>
                      {durationInMins(details.duration, strings)}
                    </Duration>
                  </>
                )}
              </Tags>
            </ListCardDescription>
          </ListCard>
        </CustomLink>
      ) : (
        <ListCard>
          <ListCardTime>
            <Time isOnNow={isOnNowCurrent}>{details.time}</Time>
            {isOnNowCurrent && <OnNow>{strings.onNow}</OnNow>}
            <Unavailable>{strings.unavailable}</Unavailable>
          </ListCardTime>
          <Image
            src={SEARCH_IMG_THUMBNAIL_URL + details.thumbnail}
            alt={details.title}
            onError={(e) =>
              imageFallback(
                e,
                imageFallback(e, SEARCH_IMG_THUMBNAIL_URL + 'clic-c.jpg')
              )
            }
          />
          <ListCardDescription>
            {isChild && <ParentTitle>{parent}</ParentTitle>}
            <Title>{details.title}</Title>
            <BillingDesc>{details.short_billing}</BillingDesc>

            <Tags>
              <ListItemSubtitlesV2 details={details} strings={strings} />
              {details.duration && (
                <>
                  <IconV2 type="custom" customLabel="timer" size="small" />
                  <Duration>
                    {durationInMins(details.duration, strings)}
                  </Duration>
                </>
              )}
            </Tags>
          </ListCardDescription>
        </ListCard>
      )}
    </ListCardItem>
  );
};

const ListCardItemImage = styled.img`
  position: relative;
  z-index: 10;
`;

const Image = styled.img`
  margin: 0 30px 0 71px;
  max-width: 266px;
  width: 100%;
  height: 100%;
  border-radius: 8px;
  ${(props) =>
    props.unavailable &&
    `
    opacity: .7;
  `}
`;

const CustomLink = styled(Link)`
  &:hover ${ListCardItemImage}, &:hover ${Image} {
    transform: translateY(-1px);
    box-shadow: ${(props) =>
      props.theme.name === 'light'
        ? `0px 32px 64px rgba(0, 0, 0, 0.075),
      0px 16px 32px rgba(0, 0, 0, 0.075), 0px 8px 16px rgba(0, 0, 0, 0.075),
      0px 4px 8px rgba(0, 0, 0, 0.075)`
        : `0px 2px 2px -3px #fef6e8,
            0px 18px 32px -4px rgba(164, 71, 10, 0.05),
            0px 12px 16px -6px rgba(248, 165, 27, 0.05),
            0px 6px 8px -4px rgba(252, 210, 141, 0.05),
            0px 3px 4px -2px rgba(253, 232, 198, 0.05),
            0px 14px 23px -12px rgba(164, 71, 10, 0.14)`};
    transition: transform 0.3s linear, -webkit-transform 0.3s linear;
  }
`;

const ListCard = styled.div`
  display: flex;
  flex-direction: row;
`;

const ListCardTime = styled.div`
  display: flex;
  flex-direction: column;
  min-width: ${(props) => (props.lang === 0 ? '115px' : '107px')};
`;

const ListCardItem = styled.div`
  @media screen and (min-width: 1140px) {
    margin-bottom: 3.8em;
    a:focus-visible {
      outline: none;
    }
    &:last-of-type {
      margin-bottom: 0;
    }
  }
`;

const OnNow = styled.div`
  ${(props) => props.theme.font.overline};
  margin-bottom: 8px;
  padding: 1px 8px 1px 6px;
  backdrop-filter: blur(24px);
  border-radius: 2px;
  font-size: 12px;
  line-height: 1.5em;
  font-weight: 500;
  letter-spacing: 1.1px;
  background: ${(props) => props.theme.color.statusAmber};
  color: ${(props) => props.theme.color.blackFull};
  align-self: flex-start;
`;

const Unavailable = styled.div`
  ${(props) => props.theme.font.overline};
  padding: 1px 8px 1px 6px;
  backdrop-filter: blur(24px);
  border-radius: 2px;
  font-size: 12px;
  line-height: 1.5em;
  font-weight: 500;
  letter-spacing: 1.1px;
  background: ${(props) =>
    props.theme.name === 'light'
      ? props.theme.color.unavailableLight
      : props.theme.color.unavailableDark};
  color: ${(props) =>
    props.theme.name === 'light'
      ? props.theme.color.white90
      : props.theme.color.black90};
  align-self: flex-start;
`;
const Time = styled.h3`
  flex-basis: auto;
  flex-grow: 0;
  flex-shrink: 0;
  margin: 0 0 12px;
  font-size: 24px;
  font-weight: bold;
  line-height: 30px;
  letter-spacing: -0.28px;
  color: ${(props) =>
    props.isOnNow
      ? css`
          ${(props) =>
            props.theme.name === 'light'
              ? props.theme.color.primaryAA
              : props.theme.color.primaryFull}
        `
      : css`
          ${(props) =>
            props.theme.name === 'light'
              ? `${props.theme.color.black75}`
              : `${props.theme.color.white70}`}
        `};
  ${(props) =>
    props.unavailable &&
    `
      opacity: .7;
  `};
`;
const ListCardDescription = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 60%;
  ${(props) =>
    props.unavailable &&
    `
    opacity: .7;
  `}
`;
const ParentTitle = styled.h4`
  margin: 0 0 4px;
  color: ${(props) =>
    props.theme.name === 'light'
      ? `${props.theme.color.grey75}`
      : `${props.theme.color.grey50}`};
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0.1px;
  text-transform: uppercase;
`;
const Title = styled.h3`
  margin: 0 0 8px;
  color: ${(props) =>
    props.theme.name === 'light'
      ? `${props.theme.color.black90}`
      : `${props.theme.color.whiteFull}`};
  font-size: 24px;
  line-height: 30px;
  letter-spacing: -0.28px;
`;
const BillingDesc = styled.p`
  margin-top: 0;
  color: ${(props) =>
    props.theme.name === 'light'
      ? `${props.theme.color.black75}`
      : `${props.theme.color.white75}`};
  font-size: 18px;
  line-height: 24px;
`;
const Tags = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  span:empty {
    margin-right: 5px;
    background-color: ${(props) =>
      props.theme.name === 'light'
        ? props.theme.color.grey75
        : props.theme.color.grey50};
  }
`;
const Duration = styled.span`
  color: ${(props) =>
    props.theme.name === 'light'
      ? `${props.theme.color.black90}`
      : `${props.theme.color.white70}`};
  font-size: 12px;
  font-weight: bold;
  line-height: 20px;
  letter-spacing: 0.1px;
`;

export default translate(connect(null)(ListItemV2));
